import type { TokenPair } from '@squire/api-client';

import { storage, storageKeys } from '@/constants/storage';
import { logoutAuthProviders } from '@/utils/authUtils';
import { Tracker } from '@/utils/tracking';

interface TokenResponseData {
  token: string;
  expiresAt?: number;
  refreshToken?: string;
  refreshTokenExpiresAt?: number;
}

export function buildTokenPair(data: TokenResponseData): TokenPair | null {
  if (!data.refreshToken) return null;

  return {
    accessToken: data.token,
    accessTokenExpiresAt: data.expiresAt
      ? new Date(data.expiresAt * 1000).toISOString()
      : undefined,
    refreshToken: data.refreshToken,
    refreshTokenExpiresAt: data.refreshTokenExpiresAt
      ? new Date(data.refreshTokenExpiresAt * 1000).toISOString()
      : undefined,
  };
}

/**
 * The customer id to use after a checkout. A guest becomes the paying customer,
 * so it's stored; a signed-in user keeps their own, because the payer can be
 * another customer their session gets 403s for (SQR-15987).
 */
export function adoptCheckoutCustomerId(
  payingCustomerId: string | undefined,
): string | undefined {
  const storedCustomerId =
    storage.getString(storageKeys.customerId) || undefined;
  if (storage.getString(storageKeys.authToken)) return storedCustomerId;

  if (payingCustomerId) {
    storage.set(storageKeys.customerId, payingCustomerId);
  }
  return payingCustomerId || storedCustomerId;
}

export function forceLogout(): void {
  storage.remove(storageKeys.authToken);
  storage.remove(storageKeys.refreshToken);
  storage.remove(storageKeys.accessTokenExpiresAt);
  storage.remove(storageKeys.refreshTokenExpiresAt);
  storage.set(storageKeys.customerId, '');
  storage.set(storageKeys.appointmentConfirmationCode, '');
  storage.set(storageKeys.guestUserInfo, JSON.stringify({}));

  logoutAuthProviders();

  const anonymousId = storage.getString(storageKeys.rudderstackAnonymusUserId);

  Tracker.setUserId(`anon_${anonymousId}`, {
    type: 'anonymous',
    anonymousId,
  });

  // The LaunchDarkly reset lives in `useIdentifyCustomer` — only the provider
  // knows how `identify-guest-contexts` resolved this session. SQR-15071.
}
